import React, { useState, useEffect, useContext } from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useDeleteCustomField, useFetchCustomFields } from 'app/data/generic-queries';
import CreateCustomFieldDialog from './dialogs/CreateCustomFieldDialog';
import ManageCustomFieldsDialog from './dialogs/ManageCustomFieldsDialog';
import CustomField from './form-fields/CustomField';
import { mergeCustomFields, groupCustomFieldsByCategory } from './utils';
import { useConfirmationDialog } from 'app/hooks/useConfirmationDialog';
import { NotificationContext } from 'app/NotificationContext';
import { userHasPermission } from 'app/../utils';
import { ManageCustomFields } from 'app/custom-widgets/PermissionsSelector/config/permissions';

const UserCustomFields = ({ userRole: role, user, fetchFields }) => {
    const { showSnackbar } = useContext(NotificationContext);
    const { control } = useFormContext();
    const [editCustomFieldModal, setEditCustomFieldModal] = useState(null);
    const [manageCustomFieldsModal, setManageCustomFieldsModal] = useState(null);
    const { ConfirmationDialog, confirmAsync } = useConfirmationDialog();
    const { mutateAsync: deleteCustomField } = useDeleteCustomField({ type: 'user', key: 'custom-fields' });
    const { data: availableCustomFields, isLoading: isLoadingAvailableCustomFields } = useFetchCustomFields({
        queryParams: { where: { role } },
        type: 'user',
        enabled: !!user && fetchFields,
        refetchOnMount: 'always',
    });
    const canManageCustomFields = userHasPermission(ManageCustomFields.value);

    const { fields, replace } = useFieldArray({
        control,
        name: 'custom_fields',
    });

    useEffect(() => {
        // If component is responsible for fetching fields, do so and merge with existing fields
        if (!isLoadingAvailableCustomFields && user && availableCustomFields && fetchFields) {
            const custom_fields = mergeCustomFields(user.custom_fields ?? [], availableCustomFields ?? []);
            replace(custom_fields);
        }
    }, [user, availableCustomFields, isLoadingAvailableCustomFields, fetchFields, replace]);

    const handleEdit = (customField) => setEditCustomFieldModal(customField);
    const handleDelete = async (customField) => {
        const result = await confirmAsync({
            title: 'Delete Custom Field',
            message: 'Warning! Deleting this custom field will remove it from all users',
            variant: 'delete',
            asyncFn: () => deleteCustomField({ id: customField._id }),
        });

        if (result instanceof Error) {
            showSnackbar({ message: 'An error occurred while deleting the custom field. Please try again or contact support if the problem persists.', severity: 'error' });
        } else if (result !== false) {
            showSnackbar({ message: 'Custom field deleted successfully', severity: 'success' });
        }
    };

    return (
        <>
            <Grid container>
                <Grid item sx={{ my: 1 }} xs={9}>
                    <Typography variant="h6" className="mt-16 mb-16">
                        Custom Fields
                    </Typography>
                </Grid>
                {canManageCustomFields && (
                    <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
                        <Button variant="outlined" onClick={() => setManageCustomFieldsModal(true)}>
                            Manage Fields
                        </Button>
                    </Grid>
                )}
                {groupCustomFieldsByCategory(fields).map((group) => (
                    <React.Fragment key={group.category}>
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" color="text.secondary">
                                {group.label}
                            </Typography>
                        </Grid>
                        {group.items.map(({ field, index }) => (
                            <Grid item sx={{ my: 1 }} xs={12} key={field.id}>
                                <CustomField customField={field} fieldIndex={index} handleEdit={() => handleEdit(field)} handleDelete={() => handleDelete(field)} />
                            </Grid>
                        ))}
                    </React.Fragment>
                ))}
            </Grid>
            <ManageCustomFieldsDialog
                open={!!manageCustomFieldsModal}
                onClose={() => setManageCustomFieldsModal(false)}
                role={role}
                handleEdit={(field) => handleEdit(field)}
                handleDelete={(field) => handleDelete(field)}
                // wrap
            />
            <CreateCustomFieldDialog open={!!editCustomFieldModal} onClose={() => setEditCustomFieldModal(null)} defaultValues={editCustomFieldModal} role={role} />
            <ConfirmationDialog />
        </>
    );
};

export default UserCustomFields;
